import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '../../common/Toast/toast';
import { confirm } from '../../common/Confirm/confirm';
import NewCustomAsyncSelectDropdown from '../../common/NewCustomAsyncSelectDropdown.jsx';
import { getBatchById, updateBatch } from '../../../../services/BatchService.js';
import { getAllStudents } from '../../../../services/StudentFromBillingService.js';
import { useAuth } from '../../../../context/AuthContext.js';

/**
 * One batch's roster, managed in place: who is in it, take someone out,
 * put someone in, save once.
 *
 * Built for the batch report's Manage step, and free of that page: it
 * takes a batch id and loads everything else itself. The roster comes
 * from GET /batches/:id?includeStudents=true, whose join config replaces
 * the batch's `userId` CSV with the student records -- the same place
 * Waypoint's batch view reads it from. Saving is the PUT the batch form
 * already makes: the new `userId` CSV plus `addUserIds` / `removeUserIds`,
 * which is what tells the API to update each student's own batchIds.
 *
 * Nothing is sent until Save. Removing is a mark on the row that can be
 * undone, and asks once, through the house confirm, when saved.
 */

const PAGE_SIZE = 20;

const toRosterRow = (s) => ({
  id: String(s.id || s._id || ''),
  name: s.name || s.studentName || 'Unnamed student',
  email: s.email || s.studentEmail || '',
  phone: s.phone || s.mobile || '',
});

const BatchStudentsManager = ({ batchId, onSaved }) => {
  const { user: authUser } = useAuth();

  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [removed, setRemoved] = useState(() => new Set());
  const [toAdd, setToAdd] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    setFailed(false);
    try {
      const res = await getBatchById(batchId, { includeStudents: true });
      const entity = res?.data?.data ?? res?.data ?? res;
      const list =
        [entity?.students, entity?.studentIds, entity?.userId].find(
          (l) => Array.isArray(l) && l.length,
        ) || [];
      setRoster(
        list
          .filter((s) => s && typeof s === 'object')
          .map(toRosterRow)
          .filter((s) => s.id)
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      setRemoved(new Set());
      setToAdd([]);
    } catch (_) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    load();
  }, [load]);

  // ── The student picker: every student, paged and searched by the API ──
  const [options, setOptions] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const pageRef = useRef(1);
  const termRef = useRef('');
  const hasMoreRef = useRef(true);
  const busyRef = useRef(false);
  const seqRef = useRef(0);
  const typingRef = useRef(null);

  const fetchOptions = useCallback(async (term, page) => {
    if (page > 1 && busyRef.current) return;
    busyRef.current = true;
    const mine = ++seqRef.current;
    setOptionsLoading(true);
    try {
      // receiptStatusOnly false: anyone on record can join a batch, not
      // only students with a receipt.
      const { data, pagination } = await getAllStudents(page, PAGE_SIZE, term, '', '', '', false);
      if (mine !== seqRef.current) return;
      const next = (Array.isArray(data) ? data : []).map((s) => {
        const row = toRosterRow(s);
        return { value: row.id, label: `${row.name} - ${row.email || 'no email'}`, original: s };
      });
      hasMoreRef.current =
        Boolean(pagination?.hasNext) || page < Number(pagination?.totalPages || 0);
      pageRef.current = page;
      termRef.current = term;
      setOptions((prev) => (page > 1 ? [...prev, ...next] : next));
    } catch (_) {
      // Leave what is listed; the next search asks again.
    } finally {
      busyRef.current = false;
      if (mine === seqRef.current) setOptionsLoading(false);
    }
  }, []);

  useEffect(() => () => clearTimeout(typingRef.current), []);

  const handleInput = (value) => {
    clearTimeout(typingRef.current);
    typingRef.current = setTimeout(() => fetchOptions(String(value || '').trim(), 1), 400);
  };

  const rosterIds = useMemo(() => new Set(roster.map((s) => s.id)), [roster]);
  // Someone already in the batch is not offered again.
  const pickable = useMemo(
    () => options.filter((o) => !rosterIds.has(o.value)),
    [options, rosterIds],
  );

  const toggleRemove = (id) =>
    setRemoved((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const addIds = toAdd.map((o) => o.value).filter((id) => id && !rosterIds.has(id));
  const removeIds = [...removed];
  const dirty = addIds.length > 0 || removeIds.length > 0;
  // Saving sends the whole roster, so nothing is added or saved until the
  // roster is here: saved while it was still loading, a batch of 70 went
  // out as the one student just picked.
  const rosterReady = !loading && !failed;

  const discard = () => {
    setRemoved(new Set());
    setToAdd([]);
  };

  const save = async () => {
    if (!dirty || saving || !rosterReady) return;
    if (removeIds.length) {
      const answer = await confirm({
        title: `Remove ${removeIds.length} student${removeIds.length === 1 ? '' : 's'} from this batch?`,
        text: 'They will no longer see this batch, its classes or its chat group.',
        confirmButtonText: 'Remove and save',
        tone: 'danger',
      });
      if (!answer.isConfirmed) return;
    }

    setSaving(true);
    try {
      const finalIds = [...roster.map((s) => s.id).filter((id) => !removed.has(id)), ...addIds];
      await updateBatch(batchId, {
        userId: finalIds.join(','),
        addUserIds: addIds.join(','),
        removeUserIds: removeIds.join(','),
        updatedBy: authUser?.id || 'NA',
      });
      toast.success('Students updated');
      await load();
      onSaved?.();
    } catch (_) {
      toast.error('Could not update the students. Nothing was changed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* Discard and Save sit at the top of the tab, above both lists, so
          they are in reach however long the roster below runs -- at the
          foot of a forty-student list they were a scroll away from the
          picker the change was made in. */}
      <div className="pg-filters-actions">
        <button type="button" className="pg-btn" onClick={discard} disabled={!dirty || saving}>
          Discard changes
        </button>
        <button
          type="button"
          className="pg-btn pg-btn--main"
          onClick={save}
          disabled={!dirty || saving || !rosterReady}
        >
          {saving
            ? 'Saving...'
            : dirty
              ? `Save (${addIds.length} in, ${removeIds.length} out)`
              : 'Save students'}
        </button>
      </div>

      <section className="pgm-section">
        <h3 className="pgm-section-title">
          Add students
          <span className="pgm-section-count">{addIds.length}</span>
        </h3>
        <div className="pg-field">
          <label className="pg-label">Find a student</label>
          <NewCustomAsyncSelectDropdown
            isMulti
            options={pickable}
            value={toAdd}
            onChange={(selected) => setToAdd(Array.isArray(selected) ? selected : [])}
            placeholder={rosterReady ? "Search by name or email..." : "Waiting for the batch's students..."}
            isDisabled={!rosterReady}
            isSearchable
            searchInControl
            isLoading={optionsLoading}
            onInputChange={handleInput}
            onMenuOpen={() => {
              if (!options.length) fetchOptions('', 1);
            }}
            onMenuScrollToBottom={() => {
              if (hasMoreRef.current && !busyRef.current) {
                fetchOptions(termRef.current, pageRef.current + 1);
              }
            }}
            loadingMessage="Loading students..."
            noOptionsMessage="No students found"
            noSearchResultsMessage="No students found"
          />
        </div>
      </section>

      <section className="pgm-section">
        <h3 className="pgm-section-title">
          In this batch
          <span className="pgm-section-count">{roster.length}</span>
        </h3>

        {loading ? (
          <div className="pg-state">
            <span className="pg-spinner" role="status" aria-hidden="true" />
            <p className="pg-state-text">Loading the students...</p>
          </div>
        ) : failed ? (
          <p className="pgm-empty">The students could not be loaded. Close this and try again.</p>
        ) : roster.length === 0 ? (
          <p className="pgm-empty">No students are in this batch yet.</p>
        ) : (
          <div className="pgm-tablescroll">
            <table className="pgm-table pgm-table--values">
              <thead>
                <tr>
                  <th className="pgm-num">#</th>
                  <th>Student</th>
                  <th>Mobile</th>
                  <th aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {roster.map((s, i) => {
                  const marked = removed.has(s.id);
                  return (
                    <tr key={s.id}>
                      <td className="pgm-num">{i + 1}</td>
                      <td>
                        <span className="pgm-person-id">
                          <span className="pgm-person-name">
                            {s.name}{' '}
                            {marked && <span className="pgm-pill pgm-pill--bad">Removing</span>}
                          </span>
                          <span className="pgm-person-mail">{s.email || 'No email on record'}</span>
                        </span>
                      </td>
                      <td className="pgm-tel">{s.phone || '—'}</td>
                      <td>
                        <button
                          type="button"
                          className={`pgm-btn${marked ? '' : ' pgm-btn--danger'}`}
                          onClick={() => toggleRemove(s.id)}
                          aria-pressed={marked}
                        >
                          {marked ? 'Undo' : 'Remove'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
};

export default BatchStudentsManager;
